// src/components/EducationReportDashboard.jsx
// 📊 教育レポートダッシュボードコンポーネント

import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

const EducationReportDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [educationData, setEducationData] = useState({
    totalAssignments: 0,
    observationCount: 0,
    guidedPracticeCount: 0,
    supervisionCount: 0,
    assessmentCount: 0,
    utilizationRate: 0,
    recentActivities: []
  });
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30日前
    end: new Date().toISOString().split('T')[0] // 今日
  });
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadEducationData();
  }, [dateRange]);

  const loadEducationData = async () => {
    setLoading(true);
    try {
      // 教育履歴データを取得
      const historyQuery = query(
        collection(db, 'educationHistory'),
        where('date', '>=', dateRange.start),
        where('date', '<=', dateRange.end),
        orderBy('date', 'desc'),
        limit(100)
      );
      
      const historySnapshot = await getDocs(historyQuery);
      const histories = historySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 統計を計算
      const stats = {
        totalAssignments: histories.length,
        observationCount: histories.filter(h => h.type === 'first_observation').length,
        guidedPracticeCount: histories.filter(h => h.type === 'guided_practice').length,
        supervisionCount: histories.filter(h => h.type === 'supervised_independence').length,
        assessmentCount: histories.filter(h => h.type === 'final_assessment').length,
        utilizationRate: histories.length > 0 ? 
          (histories.filter(h => h.completed).length / histories.length * 100).toFixed(1) : 0,
        recentActivities: histories.slice(0, 10)
      };

      setEducationData(stats);
    } catch (error) {
      console.error('教育データ読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEducationTypeLabel = (type) => {
    const labels = {
      first_observation: '初回見学',
      guided_practice: '指導下実践',
      supervised_independence: '監督下独立',
      final_assessment: '最終評価'
    };
    return labels[type] || type;
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return '#10b981';
    if (progress >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const styles = {
    container: {
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
      fontFamily: 'sans-serif',
      backgroundColor: '#f8fafc',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    dateControls: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center'
    },
    dateInput: {
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem'
    },
    refreshButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#4F46E5',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    tabNavigation: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
      backgroundColor: 'white',
      padding: '0.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    tab: {
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s',
      backgroundColor: 'transparent',
      color: '#6b7280'
    },
    activeTab: {
      backgroundColor: '#4F46E5',
      color: 'white'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem'
    },
    statCard: {
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    statIcon: {
      width: '3rem',
      height: '3rem',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem'
    },
    statContent: {
      flex: 1
    },
    statValue: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#1f2937'
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem'
    },
    chartContainer: {
      backgroundColor: 'white',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      marginBottom: '2rem'
    },
    progressBar: {
      width: '100%',
      height: '0.5rem',
      backgroundColor: '#e5e7eb',
      borderRadius: '0.25rem',
      overflow: 'hidden',
      marginTop: '0.5rem'
    },
    progressFill: {
      height: '100%',
      transition: 'width 0.3s ease'
    },
    activityList: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    },
    activityItem: {
      padding: '1rem 1.5rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    }
  };

  const renderOverview = () => (
    <div>
      {/* 統計カード */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#dbeafe', color: '#1e40af' }}>
            📚
          </div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{educationData.totalAssignments}</div>
            <div style={styles.statLabel}>総教育配置数</div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#dcfce7', color: '#166534' }}>
            👁️
          </div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{educationData.observationCount}</div>
            <div style={styles.statLabel}>見学配置</div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7', color: '#92400e' }}>
            🤝
          </div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{educationData.guidedPracticeCount}</div>
            <div style={styles.statLabel}>指導下実践</div>
          </div>
        </div>

        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#f3e8ff', color: '#7c3aed' }}>
            📊
          </div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{educationData.utilizationRate}%</div>
            <div style={styles.statLabel}>完了率</div>
          </div>
        </div>
      </div>

      {/* 教育タイプ別グラフ */}
      <div style={styles.chartContainer}>
        <h3 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>📈 教育タイプ別実績</h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {[
            { label: '初回見学', count: educationData.observationCount, color: '#3b82f6' },
            { label: '指導下実践', count: educationData.guidedPracticeCount, color: '#10b981' },
            { label: '監督下独立', count: educationData.supervisionCount, color: '#f59e0b' },
            { label: '最終評価', count: educationData.assessmentCount, color: '#ef4444' }
          ].map((item, index) => {
            const percentage = educationData.totalAssignments > 0 ? 
              (item.count / educationData.totalAssignments * 100) : 0;
            
            return (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ minWidth: '100px', fontSize: '0.875rem', color: '#374151' }}>
                  {item.label}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.progressBar}>
                    <div 
                      style={{ 
                        ...styles.progressFill, 
                        width: `${percentage}%`,
                        backgroundColor: item.color
                      }} 
                    />
                  </div>
                </div>
                <div style={{ minWidth: '60px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '500' }}>
                  {item.count} ({percentage.toFixed(1)}%)
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderActivities = () => (
    <div style={styles.activityList}>
      <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
        <h3 style={{ margin: 0, color: '#1f2937' }}>🕒 最近の教育活動</h3>
      </div>
      {educationData.recentActivities.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          選択した期間に教育活動がありません
        </div>
      ) : (
        educationData.recentActivities.map((activity, index) => (
          <div key={index} style={styles.activityItem}>
            <div>
              <div style={{ fontWeight: '500', color: '#1f2937' }}>
                {getEducationTypeLabel(activity.type)}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                指導者: {activity.instructorName || '未設定'} | 
                学習者: {activity.learnerName || '未設定'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                {activity.date}
              </div>
              <div style={{ 
                fontSize: '0.75rem', 
                color: activity.completed ? '#10b981' : '#f59e0b',
                fontWeight: '500'
              }}>
                {activity.completed ? '完了' : '進行中'}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: '2rem', 
              height: '2rem', 
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #4F46E5',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }} />
            <div>教育データを読み込み中...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <h1 style={styles.title}>📊 教育レポートダッシュボード</h1>
        <div style={styles.dateControls}>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            style={styles.dateInput}
          />
          <span style={{ color: '#6b7280' }}>〜</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            style={styles.dateInput}
          />
          <button
            onClick={loadEducationData}
            style={styles.refreshButton}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#4338ca'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#4F46E5'}
          >
            🔄 更新
          </button>
        </div>
      </div>

      {/* タブナビゲーション */}
      <div style={styles.tabNavigation}>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'overview' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('overview')}
        >
          📊 概要
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'activities' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('activities')}
        >
          📝 活動履歴
        </button>
        <button
          style={{
            ...styles.tab,
            ...(activeTab === 'trends' ? styles.activeTab : {})
          }}
          onClick={() => setActiveTab('trends')}
        >
          📈 トレンド分析
        </button>
      </div>

      {/* コンテンツ */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'activities' && renderActivities()}
      {activeTab === 'trends' && (
        <div style={styles.chartContainer}>
          <h3 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>📈 トレンド分析</h3>
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            トレンド分析機能は近日公開予定です
            <br />
            <small>グラフライブラリ統合後に実装予定</small>
          </div>
        </div>
      )}

      {/* CSS アニメーション */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default EducationReportDashboard;